import React from 'react';
import styled from 'styled-components';
import { graphql, Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';

import SEO from '../../components/SEO';
import Body from '../../components/layout/Body';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import NonStretchedImage from '../../components/layout/NonStretchedImage';
import RelativeWrapper from '../../components/layout/RelativeWrapper';
import ContentWrapper from '../../components/layout/ContentWrapper';

import { ViewportBreakpoints } from '../../components/cssConstants';

import FeatureListBreaker, { Features } from '../../components/cta/FeatureListBreaker';

import HeroH1 from '../../components/hero/HeroH1';
import HeroH2 from '../../components/hero/HeroH2';

import H2 from '@rotaready/frecl/build/H2';
import H3 from '@rotaready/frecl/build/H3';
import H4 from '@rotaready/frecl/build/H4';
import Text from '@rotaready/frecl/build/Text';
import Button from '@rotaready/frecl/build/Button';
import FlexContainer from '@rotaready/frecl/build/FlexContainer';
import UL from '@rotaready/frecl/build/UL';
import TextListItem from '@rotaready/frecl/build/TextListItem';
import LinkWithArrow from '@rotaready/frecl/build/LinkWithArrow';

const DarkWrapper = styled.div`
  background-color: ${({ theme: { colors } }) => colors.brand180};
  background-position: right bottom;
  background-repeat: no-repeat;
  background-size: 1440px 519px;
  background-image: url(${props => props.backgroundImage});
`;

const Hero = styled.div`
  padding: 80px 0;

  @media (min-width: ${ViewportBreakpoints.tabletPortraitUpper}) {
    padding: 120px 0;
  }
`;

const HeroInnerContainer = styled(FlexContainer)`
  flex-direction: column;
  width: 100%;
  text-align: center;

  @media (min-width: ${ViewportBreakpoints.tabletPortraitUpper}) {
    flex-direction: row;
    text-align: left;
  }
`;

const HeroBody = styled.div`
  @media (min-width: ${ViewportBreakpoints.tabletPortraitUpper}) {
    width: 45%;
    margin-right: 80px;
  }
`;

const HeroIllustration = styled.div`
  flex-grow: 1;

  .gatsby-image-wrapper {
    width: 100%;
    margin: 0 auto;
  }
`;

const CtaButtons = styled.div`
  margin: 30px auto;
  max-width: 180px;

  button {
    width: 100%;
    margin: 0 0 20px 0;
  }

  @media (min-width: ${ViewportBreakpoints.tabletPortraitUpper}) {
    margin-top: 40px;
    max-width: unset;

    button {
      width: unset;
      margin: 0 20px 0 0;
    }
  }
`;

const SectionTitle = styled(H2)`
  margin-bottom: 60px;
  text-align: center;
`;

const PayrollOptions = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${ViewportBreakpoints.tabletPortraitUpper}) {
    flex-direction: row;
  }
`;

const PayrollOption = styled.div`
  flex: 1 50%;
  border-top: 4px solid ${({ theme: { colors } }) => colors.brand60};
  box-sizing: content-box;
  padding: 30px;
  margin: 0 0 40px 0;

  > * {
    margin-bottom: 20px;
  }

  @media (min-width: ${ViewportBreakpoints.tabletPortraitUpper}) {
    padding: 40px 60px;
    margin: 0 40px;
  }
`;

const BringYourOwn = styled(PayrollOption)``;

const FullyManaged = styled(PayrollOption)`
  background-color: ${({ theme: { colors } }) => colors.grey10};
  margin-bottom: 0;
`;

const IntegrationLogoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  padding: 10px 0;

  @media (min-width: ${ViewportBreakpoints.desktopUpper}) {
    padding: 20px 0;
    flex-direction: row;
  }
`;

const Logo = styled.div`
  text-align: center;
  flex: 1 1 auto;
  padding: 10px 0;

  > div {
    margin: 0 auto;
  }

  @media (min-width: ${ViewportBreakpoints.desktopUpper}) {
    padding: 0;
  }
`;

const NeedAdviceSection = styled.div`
  text-align: center;
  margin: 0 auto;

  @media (min-width: ${ViewportBreakpoints.tabletPortraitUpper}) {
    width: 60%;
  }
`;

const NeedAdviceText = styled(Text)`
  margin: 20px 0 40px 0;
`;

const header = {
  backgroundColor: 'brand180',
  invertColors: true,
};

const PayrollWrapper = styled.div`
  padding: 100px 0;

  @media (min-width: ${ViewportBreakpoints.tabletPortraitUpper}) {
    padding: 60px 0;
  }
`;

class PayrollFeaturePage extends React.Component {
  openIntercom = () => {
    Intercom('show');
  };

  render() {
    const {
      data,
    } = this.props;

    return (
      <Body header={header}>
        <SEO
          title="Payroll - Rotaready"
          description="Rotaready makes payroll painless, with easy integrations, automatic timesheets, live wage calculations and an optional fully managed payroll bureau."
          url="payroll"
        />
        <DarkWrapper backgroundImage={data.heroBackgroundMask.childImageSharp.fixed.src}>
          <ResponsiveContainer>
            <Hero>
              <HeroInnerContainer>
                <HeroBody>
                  <HeroH1 color="brand" text="Payroll" />
                  <HeroH2 color="white" text="Perfect payroll, every time" />

                  <H4 uistyle="white">
                    Make the headache of payroll a thing of the past with Rotaready's payroll software. With easy integrations, auto-generated timesheets, live wage calculations and cost control, <Link to="/" style="color:inherit;">Rotaready</Link> helps make it as painless as possible.
                    <br />
                    <br />
                    Our payroll system gives you the option to either take control of your payroll in-house, integrate Rotaready with your existing payroll partner or opt for our fully managed bureau, so you can choose what works best for your business.
                  </H4>

                  <CtaButtons>
                    <Link to="/demo">
                      <Button uistyle="primary" text="Request demo" size="lg" />
                    </Link>
                    <Link to="/contact">
                      <Button uistyle="white" text="Speak to the team" size="lg" ghost borderless />
                    </Link>
                  </CtaButtons>
                </HeroBody>

                <HeroIllustration>
                  <NonStretchedImage fluid={data.heroImage.childImageSharp.fluid} />
                </HeroIllustration>
              </HeroInnerContainer>
            </Hero>
          </ResponsiveContainer>
        </DarkWrapper>

        
        <RelativeWrapper backgroundColor="white">
          <ResponsiveContainer>
            <PayrollWrapper>
              <SectionTitle uistyle="brand160">Choose what works best for your business</SectionTitle>

              <PayrollOptions>
                <BringYourOwn>
                  <H3 uistyle="brand160" text="Bring your own payroll" />

                  <Text uistyle="brand160" size="xl" text="You dictate how your timesheets work with payroll" />

                  <UL size="xl" uistyle="normal" iconUistyle="primary">
                    <TextListItem text="Export perfect timesheets directly from Rotaready to give to your payroll department" />
                    <TextListItem text="Bespoke integration with your current payroll partner: self managed or fully managed" />
                    <TextListItem text="Or, switch on a free integration with one of our real-time partners:" />
                  </UL>

                  <IntegrationLogoWrapper>
                    <Logo>
                      <GatsbyImage
                        image={data.payrollSage.childImageSharp.gatsbyImageData}
                        alt="Sage logo"
                      />
                    </Logo>
                    <Logo>
                      <GatsbyImage
                        image={data.payrollXero.childImageSharp.gatsbyImageData}
                        alt="Xero logo"
                      />
                    </Logo>
                    <Logo>
                      <GatsbyImage
                        image={data.payrollPayCaptain.childImageSharp.gatsbyImageData}
                        alt="PayCaptain logo"
                      />
                    </Logo>
                  </IntegrationLogoWrapper>

                  <LinkWithArrow
                    direction="right"
                    to="/integrations"
                    text="Find out more about integrations"
                    render={props => <Link {...props} />}
                  />
                </BringYourOwn>

                <FullyManaged>
                  <H3 uistyle="brand160" text="Fully managed bureau" />

                  <Text uistyle="brand160" size="xl" text="Sit back and relax, we'll take care of everything for you" />

                  <UL size="xl" uistyle="normal" iconUistyle="primary">
                    <TextListItem text="Perfect timesheets automatically fed to payroll" />
                    <TextListItem text="HMRC & third party payment processing" />
                    <TextListItem text="Pension auto enrolment" />
                    <TextListItem text="Digital payslips in the Rotaready app" />
                    <TextListItem text="P35, P45 and P60 processing" />
                    <TextListItem text="Fully secure & HMRC accredited" />
                    <TextListItem text="BACS payments" />
                    <TextListItem text="Dedicated payroll manager" />
                    <TextListItem text="Fully certified service" />
                  </UL>
                </FullyManaged>
              </PayrollOptions>
            </PayrollWrapper>
          </ResponsiveContainer>
        </RelativeWrapper>
        

        <FeatureListBreaker
          rows={1}
          excludeFeature={Features.PAYROLL}
          headingText="Other features"
        />


        <RelativeWrapper backgroundColor="white">
          <ResponsiveContainer>
            <ContentWrapper>
              <NeedAdviceSection>
                <H2 uistyle="brand160" text="Need some advice?" />
                <NeedAdviceText size="xl" uistyle="grey" text="No problem. Drop us an email or click the button below to start a live chat - our team will be delighted to help with any payroll queries you may have." />

                <Button onClick={this.openIntercom} uistyle="primary" size="lg" text="Chat with us now" />
              </NeedAdviceSection>
            </ContentWrapper>
          </ResponsiveContainer>
        </RelativeWrapper>

        
      </Body>
    );
  }
}

export default PayrollFeaturePage;

export const query = graphql`
  query {
    heroImage: file(relativePath: { eq: "misc/hero-payroll.png" }) {
      childImageSharp {
        fluid(maxWidth: 548, quality: 100) {
          ...GatsbyImageSharpFluid_withWebp
          presentationWidth
        }
      }
    }

    heroBackgroundMask: file(relativePath: { eq: "misc/hero-mask-brand180.png" }) {
      childImageSharp {
        fixed(height: 519, width: 1440, quality: 100) {
          src
        }
      }
    }

    payrollSage: file(relativePath: { eq: "integrations/payroll-sage.png" }) {
      childImageSharp {
        gatsbyImageData(height: 29, width: 74, quality: 100)
      }
    }

    payrollXero: file(relativePath: { eq: "integrations/payroll-xero.png" }) {
      childImageSharp {
        gatsbyImageData(height: 44, width: 44, quality: 100)
      }
    }

    payrollPayCaptain: file(relativePath: { eq: "integrations/payroll-paycaptain.webp" }) {
      childImageSharp {
        gatsbyImageData(height: 36, width: 184, quality: 100)
      }
    }
  }
`;
